import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServerSort } from '../../hooks/useServerSort';
// EntityAvatar stays for the person who reported — a per-row identity mark doing its actual job.
import { Card, Table, Tag, Typography, EmptyState, Switch, Space, Tooltip, StatCard, StatusBadge, EntityAvatar, Button } from '../../ui';
import { orderStatusLabel } from '../../lib/statusTone';
import { PackageX, ArrowLeftRight, Ban, Shuffle, X, Wrench, Eye } from 'lucide-react';
import type { TagTone } from '../../ui';
import { Link } from 'react-router-dom';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { useAuthStore } from '../../store/auth';
import { ShortfallActionModal } from './ShortfallActionModal';
import {
  OUTCOME_LABEL, NEXT_STEP, VIA_LABEL, AGE_TONE, daysSince, parseSnapshot, type ShortfallRow,
} from './shortfallVocab';

/**
 * Fulfillment V2 · Phase 2.5 — the persisted shortfall QUEUE. Planners/admins see every open shortfall
 * (their worklist); a Warehouse Manager sees only shortfalls at their assigned warehouse(s), read-only.
 * There is no "resolve" action — a shortfall clears automatically the moment the order is no longer short
 * (consolidated / restocked / reassigned / fulfilled) or the order is cancelled. Planners act on the ORDER.
 */
export function ShortfallsQueuePage() {
  const [showResolved, setShowResolved] = useState(false);
  /* The row clicked open. A shortfall IS a record — what is short, where, for how long, and the one
     move that clears it — and until now the queue could only hand the reader an order number and
     send them to a different screen to act on it. */
  const [detailFor, setDetailFor] = useState<ShortfallRow | null>(null);
  /* Which job the reader is triaging right now. Driven by the KPI band: the tiles were four numbers
     that named the three jobs this queue exists to route, and clicking one is the obvious way to see
     just those rows — reading the count and then hunting for its rows by eye is work the page can do. */
  const [outcome, setOutcome] = useState<string | null>(null);
  const role = useAuthStore((s) => s.user?.role);
  const isWhm = role === 'WAREHOUSE_MANAGER';
  // Server-side sort — the queue is capped at 500 rows, and clearing the sort returns to the triage
  // order the backend defaults to (still-open first, biggest pending value first).
  const { sortState, onSortChange, sort } = useServerSort({
    fieldMap: { shortSnapshot: 'pendingTotal', reported: 'reportedByName' },
  });
  const { data, isLoading } = useQuery({
    queryKey: ['shortfalls', showResolved ? 'all' : 'open', sort],
    queryFn: () => request<ShortfallRow[]>({
      url: '/shortfalls',
      params: { ...(showResolved ? { status: 'all' } : {}), ...(sort ? { sort } : {}) },
    }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const allRows = data ?? [];
  const rows = outcome ? allRows.filter((r) => r.outcome === outcome) : allRows;
  /* One tile per JOB, not per severity: what a planner wants to know is how much of the backlog is
     "move stock I already own" versus "there is none to move". Counted over the loaded queue. */
  const open = allRows.filter((r) => r.status === 'OPEN');
  const byOutcome = (k: string) => open.filter((r) => r.outcome === k).length;
  /* How long the oldest one has been blocking. A count says how big the backlog is; only age says
     whether it is getting away from you, and it was previously only readable by doing date
     arithmetic against the Reported column in your head, row by row. */
  const oldest = open.reduce((m, r) => Math.max(m, daysSince(r.reportedAt) ?? 0), 0);
  /* Picking a job also drops back to the open rows — the tiles count OPEN only, so leaving resolved
     ones on screen would show more rows than the number that was just clicked. */
  const pick = (k: string) => { setOutcome((cur) => (cur === k ? null : k)); setShowResolved(false); };

  return (
    <div>
      <Typography.Title level={1} style={{ marginBottom: 2 }}>Shortfalls</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {isWhm
          ? 'Shortfalls at your warehouse(s). These are resolved by a planner — they clear automatically once the order is no longer short.'
          : 'Orders that can’t be fully packed at their warehouse. Open a row to consolidate stock, add stock, or reassign the warehouse — each entry clears automatically once the order is no longer short.'}
      </Typography.Paragraph>
      <div className="mb-3.5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        {/* Each tile is the filter for the job it names — click it to see just those rows, click it
            again to come back. The first tile is the way back to everything. */}
        <StatCard
          tone={open.length ? 'danger' : 'brand'} icon={<PackageX size={18} />} label="Open shortfalls" value={data ? open.length : '—'}
          hint={!open.length ? 'every order is coverable'
            : oldest > 0 ? `oldest has waited ${oldest}d` : 'all reported today'}
          onClick={() => { setOutcome(null); setShowResolved(false); }}
          active={!outcome && !showResolved}
        />
        <StatCard
          tone="gold" icon={<ArrowLeftRight size={18} />} label="Needs transfer" value={data ? byOutcome('NEEDS_TRANSFER') : '—'}
          hint="the stock exists — at another warehouse"
          onClick={byOutcome('NEEDS_TRANSFER') ? () => pick('NEEDS_TRANSFER') : undefined}
          active={outcome === 'NEEDS_TRANSFER'}
        />
        <StatCard
          tone="sky" icon={<Shuffle size={18} />} label="Reassign warehouse" value={data ? byOutcome('FULLY_COVERED_SINGLE') : '—'}
          hint="one other site covers the whole order"
          onClick={byOutcome('FULLY_COVERED_SINGLE') ? () => pick('FULLY_COVERED_SINGLE') : undefined}
          active={outcome === 'FULLY_COVERED_SINGLE'}
        />
        <StatCard
          tone={byOutcome('INSUFFICIENT_TOTAL') ? 'danger' : 'neutral'} icon={<Ban size={18} />} label="No stock anywhere"
          value={data ? byOutcome('INSUFFICIENT_TOTAL') : '—'}
          hint={byOutcome('INSUFFICIENT_TOTAL') ? 'needs production or purchase' : 'nothing is unsourceable'}
          onClick={byOutcome('INSUFFICIENT_TOTAL') ? () => pick('INSUFFICIENT_TOTAL') : undefined}
          active={outcome === 'INSUFFICIENT_TOTAL'}
        />
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <Space>
            <Switch checked={showResolved} onCheckedChange={setShowResolved} />
            <span>Show resolved</span>
          </Space>
          {/* A filter applied from somewhere ELSE on the page has to be visible and undoable from
              here, or the table just looks short for no stated reason. */}
          {outcome && (
            <button
              type="button" onClick={() => setOutcome(null)}
              className="inline-flex items-center gap-1.5 rounded-pill border border-brand-500/40 bg-brand-500/8 px-2.5 py-1 type-caption text-brand-700 transition-colors hover:bg-brand-500/15 dark:text-brand-300"
            >
              Showing only: {OUTCOME_LABEL[outcome]?.text ?? outcome} <X className="size-3.5" />
            </button>
          )}
        </div>
        <Table<ShortfallRow>
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={rows}
          sortState={sortState}
          onSortChange={onSortChange}
          onRow={(r) => ({ onClick: () => setDetailFor(r) })}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} ${showResolved ? 'total' : 'open'}` }}
          locale={{ emptyText: <EmptyState title={outcome ? `Nothing in “${OUTCOME_LABEL[outcome]?.text ?? outcome}” right now.` : 'No open shortfalls — all orders coverable.'} /> }}
          columns={[
            {
              // No identity mark in front of either of these. The mark exists to make one row
              // findable among many by being DIFFERENT per row; pinned to a single Store (or
              // Warehouse) glyph it repeated the column header on every line and only indented
              // the values that people actually read down these two columns.
              title: 'Order', dataIndex: 'orderNo', width: 180, sorter: true,
              render: (v: string, r) => (
                <div className="min-w-0">
                  {/* The row opens the shortfall; the number still goes to the whole order, so both
                      destinations stay reachable and neither is a surprise. */}
                  <Tooltip label="Open the full order">
                    <Link to={`/orders/${r.orderId}`} className="type-label-strong" onClick={(e) => e.stopPropagation()}>{v}</Link>
                  </Tooltip>
                  <div className="truncate type-caption text-muted">{r.retailerName ?? '—'}</div>
                </div>
              ),
            },
            {
              title: 'Warehouse', dataIndex: 'warehouseName', width: 150, sorter: true,
              render: (v: string | null, r) => {
                const code = r.warehouseCode ?? v ?? String(r.warehouseId);
                // The site's full name, unless it only re-spells the code that is already above it.
                const name = v && v.toLowerCase() !== code.toLowerCase() ? v : null;
                return (
                  <div className="min-w-0">
                    <div className="truncate text-content">{code}</div>
                    {name && <div className="truncate type-caption text-muted">{name}</div>}
                  </div>
                );
              },
            },
            {
              // Sorts by the pending TOTAL — the one number behind this column's per-line chips.
              // No fixed width: this column carries the row's actual payload (one chip per short
              // product) and is the only one that grows with the data, so it takes the table's spare
              // room. It used to be capped at 200px while a mostly-empty Reported column absorbed
              // ~600px of blank, which stacked a five-product shortfall into five wrapped lines
              // beside a void.
              title: 'Short', dataIndex: 'shortSnapshot', sorter: true,
              render: (_: any, r) => {
                const lines = parseSnapshot(r.shortSnapshot);
                if (!lines.length) return <Tag tone="red">short {fmtQty(Number(r.pendingTotal))}</Tag>;
                return (
                  <Space size={[4, 4]} wrap>
                    {lines.map((l, i) => (
                      <Tag tone="red" key={i} style={{ margin: 0 }}>{fmtQty(Number(l.shortQty))} × {l.productName ?? l.productId}</Tag>
                    ))}
                  </Space>
                );
              },
            },
            {
              /* The diagnosis AND the move it implies. The chip alone told a planner which of three
                 buckets the row was in and left them to remember what that bucket means to do. */
              title: 'Why · what to do', dataIndex: 'outcome', width: 230, sorter: true,
              render: (v: string | null) => {
                const o = v ? OUTCOME_LABEL[v] : null;
                return (
                  <div className="min-w-0">
                    {o
                      ? <Tag tone={o.color as TagTone} icon={<o.Icon size={11} />}>{o.text}</Tag>
                      : (v ? <Tag>{v}</Tag> : <span className="text-subtle">—</span>)}
                    {v && NEXT_STEP[v] && (
                      <div className="mt-1 type-caption leading-snug text-muted">{NEXT_STEP[v]}</div>
                    )}
                  </div>
                );
              },
            },
            {
              /* Where the blocked order actually sits. The queue's whole point is that these orders
                 cannot move, and until now the table never said what they were stuck BEFORE — an
                 approved order waiting on stock and a packed one short at dispatch are different
                 jobs with different urgency. It rides in the payload already. */
              title: 'Order stage', dataIndex: 'orderStatus', width: 130, sorter: true,
              render: (v: string) => (v ? <StatusBadge status={v} label={orderStatusLabel(v)} /> : <span className="text-subtle">—</span>),
            },
            {
              /* How long it has blocked. Age is what ranks this worklist, and it was only readable
                 by doing date arithmetic against the Reported timestamp in your head. */
              title: 'Open for', dataIndex: 'reportedAt', width: 105, sorter: true,
              render: (_: any, r) => {
                if (r.status !== 'OPEN') {
                  const closed = daysSince(r.resolvedAt);
                  return <span className="type-caption text-subtle">{closed == null ? '—' : closed === 0 ? 'closed today' : `closed ${closed}d ago`}</span>;
                }
                const n = daysSince(r.reportedAt);
                if (n == null) return <span className="text-subtle">—</span>;
                return <Tag tone={AGE_TONE(n)}>{n === 0 ? 'today' : `${n}d`}</Tag>;
              },
            },
            {
              // Bounded now. Its content is a chip, a name and a timestamp — a fixed budget — so letting
              // it absorb every spare pixel bought nothing but the blank stripe that ran down the
              // middle of the table. The remark still gets one truncated line with the full text on hover.
              title: 'Reported', key: 'reported', width: 230, sorter: true,
              render: (_: any, r) => (
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Tag tone={r.reportedVia === 'MANUAL' ? 'blue' : 'neutral'}>{VIA_LABEL[r.reportedVia] ?? r.reportedVia}</Tag>
                    {r.reportedByName && (
                      <span className="inline-flex items-center gap-1.5">
                        <EntityAvatar name={r.reportedByName} size={20} />
                        <span className="text-content">{r.reportedByName}</span>
                      </span>
                    )}
                    {r.reportedAt && <span className="type-caption text-subtle">· {formatIst(r.reportedAt)}</span>}
                  </div>
                  {r.remarks ? (
                    <Tooltip label={r.remarks}>
                      <div className="mt-0.5 max-w-full truncate type-caption italic text-muted">“{r.remarks}”</div>
                    </Tooltip>
                  ) : null}
                </div>
              ),
            },
            {
              title: 'Status', dataIndex: 'status', width: 110, sorter: true,
              render: (v: string, r) =>
                v === 'OPEN'
                  ? <Tag tone="red">Open</Tag>
                  : (
                    <Tooltip label={r.resolution === 'AUTO_CANCELLED' ? 'Order cancelled/closed' : r.resolution === 'AUTO' ? 'Order no longer short' : r.resolution ?? ''}>
                      <Tag tone="green">Resolved</Tag>
                    </Tooltip>
                  ),
            },
            {
              /* Open-the-record, in the LAST slot on every row — the house rule for row actions, so
                 the right edge of every register is the same button. The row itself opens the record
                 too; this is the affordance that SAYS so, because a clickable row announces nothing.
                 Icon-only at 32px with a tooltip AND an aria-label carrying the full wording.
                 The icon carries the difference between the two jobs: an OPEN shortfall is work (the
                 dialog is where the fix is, so it is the filled, forward button), a resolved one is
                 a read. */
              key: 'actions', title: 'Action', width: 80, align: 'right' as const, alwaysVisible: true,
              render: (_: any, r: ShortfallRow) => {
                const live = r.status === 'OPEN';
                const label = live ? 'Handle this shortfall' : 'View this shortfall';
                return (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Tooltip label={live ? 'Handle this shortfall — reassign the warehouse, transfer stock in, or add stock' : label}>
                      <Button
                        size="sm" variant={live ? 'primary' : 'text'} aria-label={label}
                        icon={live ? <Wrench size={16} /> : <Eye size={16} className="text-muted" />}
                        onClick={() => setDetailFor(r)}
                      />
                    </Tooltip>
                  </span>
                );
              },
            },
          ]}
        />
      </Card>

      {detailFor && (
        <ShortfallActionModal row={detailFor} open={!!detailFor} onClose={() => setDetailFor(null)} />
      )}
    </div>
  );
}
